import { useEffect, useId, useState } from 'react';
import { apiClient } from '../utils/api';

export function tenantLogoSrc(value?: string | null): string | undefined {
  if (!value) return undefined;
  return value.startsWith('/api/') ? new URL(value, apiClient.client.defaults.baseURL || window.location.origin).href : value;
}

interface Props {
  tenantId?: string;
  value?: string | null;
  onChange: (value: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

/** Shared branding editor for host administration and tenant settings. */
export default function TenantLogoField({ tenantId, value, onChange, onBusyChange }: Props) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState('');
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const uploaded = value?.startsWith('/api/v1/tenants/');
  return (
    <div className="space-y-3">
      <label htmlFor={`${id}-url`} className="block text-sm font-medium text-gray-700">Logo URL</label>
      <input id={`${id}-url`} type="url" maxLength={500} value={uploaded ? '' : value || ''} disabled={busy}
        placeholder="https://esempio.it/logo.svg" className="w-full rounded-lg border border-gray-300 px-3 py-2"
        onChange={(event) => { setPreview(''); setError(''); onChange(event.target.value); }} />
      {uploaded && <p className="text-xs text-gray-500">Logo caricato nello storage del tenant.</p>}
      <label htmlFor={`${id}-file`} className="block text-sm font-medium text-gray-700">Oppure carica un logo</label>
      <input id={`${id}-file`} type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
        disabled={busy || !tenantId} className="block w-full text-sm" onChange={async (event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file || !tenantId) return;
          if (!/\.(png|jpe?g|svg)$/i.test(file.name) || file.size > 2 * 1024 * 1024) {
            setError('Carica un PNG, JPEG o SVG di massimo 2 MB'); return;
          }
          setBusy(true); onBusyChange?.(true); setError('');
          try {
            const body = new FormData(); body.append('file', file);
            const response = await apiClient.client.post<{ logo_url: string }>(`/tenants/${tenantId}/logo`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
            setPreview(URL.createObjectURL(file)); onChange(response.data.logo_url);
          } catch (error: any) {
            setError(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Impossibile caricare il logo');
          } finally { setBusy(false); onBusyChange?.(false); }
        }} />
      <p className="text-xs text-gray-500">PNG, JPEG o SVG, massimo 2 MB. {tenantId ? 'Salva le modifiche per pubblicare il logo.' : 'Crea prima il tenant per caricare un file.'}</p>
      {busy && <p role="status" className="text-sm">Caricamento…</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {(preview || value) && <div className="flex items-center gap-3">
        <img src={preview || tenantLogoSrc(value)} alt="Anteprima logo tenant" className="h-16 max-w-48 rounded border bg-gray-100 p-2 object-contain" />
        <button type="button" disabled={busy} onClick={() => { setPreview(''); onChange(''); }} className="text-sm text-red-600">Rimuovi logo</button>
      </div>}
    </div>
  );
}
